import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { erc20Abi, formatUnits, getAddress } from 'viem';
import {
    switchChain,
    writeContract,
} from 'wagmi/actions';

import { toast } from '@/components/ui/toast';
import {
    HYPER_EVM_CHAIN_ID,
    useAnchorChain,
    useFactionsAuthedApi,
    useWallet,
    walletWagmiConfig,
} from '@/features/wallet';
import { wolfswapFactionsApi } from '@/lib/api';
import { fetchBalanceLight, fetchSpeedConfig, USDC_DECIMALS } from '@/lib/api/speed';
import type { BalanceLightResponse } from '@/lib/api/speed';
import { describeErrorForUser, userErrorText } from '@/lib/errors';

import {
    chooseHyperDepositRoute,
    readEvmAllowance6,
    SELF_FUND_ABI,
} from '../services/directDeposit';
import type { HyperDepositRoute } from '../services/directDeposit';
import {
    hasUsableAdapter,
    MIN_HYPE_FOR_GAS_WEI,
    readCoreSpotUsdc8,
    readEvmUsdc6,
    readHypeBalanceWei,
} from '../services/directWithdraw';

import {
    ensureHyperEvmChain,
    waitForSuccessfulHyperEvmReceipt,
} from './hyperEvmChain';

const TOAST_DURATION = 10_000;
const READ_STALE_TIME_MS = 30_000;

/** Non-blocking confirmation that HyperCore credited the adapter; success is the receipt. */
const CORE_CREDIT_POLL_MS = 30_000;
const CORE_CREDIT_INTERVAL_MS = 3_000;

type DirectDepositStep = 'approve' | 'fund';

const STEP_LABELS: Record<DirectDepositStep, string> = {
    approve: 'Approving USDC',
    fund: 'Funding your trading balance',
};

export interface DirectDepositState {
    status: 'idle' | 'preparing' | 'running' | 'success' | 'failed';
    /** 1-based position of the step currently executing. */
    stepIndex: number;
    stepCount: number;
    stepLabel: string | null;
    error: string | null;
    txHashes: string[];
    /** What the completed run funded (display). */
    depositedUsd: number | null;
}

const INITIAL_STATE: DirectDepositState = {
    status: 'idle',
    stepIndex: 0,
    stepCount: 0,
    stepLabel: null,
    error: null,
    txHashes: [],
    depositedUsd: null,
};

const sleep = async (ms: number) =>
    new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * EOA-direct HyperEVM deposit (`selfFund`): the connected wallet signs an approval and
 * `HyperliquidManager.selfFund(amount)` on chain 999, moving its own USDC into the user's
 * adapter. No backend job, no service wallet, no bridge — see services/directDeposit.ts.
 *
 * Also owns the reads the deposit form needs to offer HyperEVM as a source at all: the EOA's
 * USDC balance there, its HYPE gas, and which of the two HyperEVM routes applies.
 *
 * The wallet is switched to HyperEVM for the duration and back to Cronos afterwards.
 */
export function useDirectDeposit({ onSuccess }: { onSuccess?: () => void } = {}) {
    const [state, setState] = useState<DirectDepositState>(INITIAL_STATE);
    const lockRef = useRef(false);
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();
    const factionsApi = useFactionsAuthedApi();
    const queryClient = useQueryClient();

    const reset = useCallback(() => setState(INITIAL_STATE), []);

    // which HyperEVM route applies + which token to move. `balance/light` 4xx's for users with
    // no custodial wallet yet; that is not an error here, it IS the saga-route signal.
    const contextQuery = useQuery({
        queryKey: ['speed', 'directDeposit', 'context', address],
        queryFn: async () => {
            const config = await fetchSpeedConfig(wolfswapFactionsApi);
            let light: BalanceLightResponse | null = null;
            try {
                light = await fetchBalanceLight(factionsApi!);
            } catch (error) {
                console.warn(
                    '[DirectDeposit] balance/light unavailable — treating as not-yet-onboarded',
                    error,
                );
            }
            return {
                usdc: light?.usdc ?? config.usdc,
                adapter: light && hasUsableAdapter(light) ? light.adapter : null,
                route: chooseHyperDepositRoute(light),
            };
        },
        enabled: !!factionsApi && !!address,
        staleTime: READ_STALE_TIME_MS,
    });

    const usdcAddress = contextQuery.data?.usdc ?? null;

    const balanceQuery = useQuery({
        queryKey: ['speed', 'directDeposit', 'balance', address, usdcAddress],
        queryFn: async () => readEvmUsdc6(usdcAddress!, address!),
        enabled: !!usdcAddress && !!address,
        staleTime: READ_STALE_TIME_MS,
    });

    const gasQuery = useQuery({
        queryKey: ['speed', 'directDeposit', 'hypeGas', address],
        queryFn: async () => readHypeBalanceWei(address!),
        enabled: !!address,
        staleTime: READ_STALE_TIME_MS,
    });

    const hypeGasOk =
        gasQuery.data === undefined
            ? null
            : gasQuery.data >= MIN_HYPE_FOR_GAS_WEI;
    const balance6 = balanceQuery.data ?? null;
    const balance = useMemo(
        () =>
            balance6 === null
                ? null
                : Number(formatUnits(balance6, USDC_DECIMALS)),
        [balance6],
    );

    // one invalidation covers all three reads above — they share the key prefix.
    const refresh = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: ['speed', 'directDeposit'],
        });
    }, [queryClient]);

    const run = useCallback(
        async (amount6: bigint) => {
            if (lockRef.current) return;
            if (!factionsApi || !address) {
                setState((prev) => ({
                    ...prev,
                    status: 'failed',
                    error: 'Connect & sign in first',
                }));
                return;
            }

            lockRef.current = true;
            const txHashes: string[] = [];
            try {
                setState({
                    ...INITIAL_STATE,
                    status: 'preparing',
                    stepLabel: 'Checking your wallet',
                });

                const [light, config] = await Promise.all([
                    fetchBalanceLight(factionsApi),
                    fetchSpeedConfig(wolfswapFactionsApi),
                ]);
                if (!hasUsableAdapter(light)) {
                    // the caller routes on the same read, so this only fires on a race
                    throw new Error(
                        'Your Snappy Wallet is not ready for a direct deposit yet, retry in a moment.',
                    );
                }

                const eoa = getAddress(address);
                const usdc = getAddress(light.usdc);
                const adapter = getAddress(light.adapter!);
                const manager = getAddress(config.manager);

                if (amount6 <= 0n) {
                    throw new Error('Enter an amount to deposit.');
                }
                const available = await readEvmUsdc6(usdc, eoa);
                if (amount6 > available) {
                    throw new Error(
                        'Amount exceeds your USDC balance on HyperEVM.',
                    );
                }
                if ((await readHypeBalanceWei(eoa)) < MIN_HYPE_FOR_GAS_WEI) {
                    throw new Error(
                        'Your wallet needs a little HYPE on HyperEVM to pay gas for the direct deposit.',
                    );
                }

                // explicit switch: injected wallets are on Cronos here, and writeContract with a
                // chainId asserts rather than switches for them.
                await ensureHyperEvmChain();

                // a wallet that already approved enough should not be asked to sign again
                const allowance = await readEvmAllowance6(usdc, eoa, adapter);
                const steps: DirectDepositStep[] =
                    allowance >= amount6 ? ['fund'] : ['approve', 'fund'];

                const coreBefore = await readCoreSpotUsdc8(adapter).catch(
                    () => null,
                );

                for (const [index, step] of steps.entries()) {
                    setState((prev) => ({
                        ...prev,
                        status: 'running',
                        stepIndex: index + 1,
                        stepCount: steps.length,
                        stepLabel: STEP_LABELS[step],
                        txHashes: [...txHashes],
                    }));

                    const hash =
                        step === 'approve'
                            ? await writeContract(walletWagmiConfig, {
                                account: eoa,
                                address: usdc,
                                abi: erc20Abi,
                                functionName: 'approve',
                                args: [adapter, amount6],
                                chainId: HYPER_EVM_CHAIN_ID,
                            })
                            : await writeContract(walletWagmiConfig, {
                                account: eoa,
                                address: manager,
                                abi: SELF_FUND_ABI,
                                functionName: 'selfFund',
                                args: [amount6],
                                chainId: HYPER_EVM_CHAIN_ID,
                            });
                    txHashes.push(hash);
                    await waitForSuccessfulHyperEvmReceipt(hash, step === 'approve' ? 'Approval' : 'Deposit');
                }

                const depositedUsd = Number(formatUnits(amount6, USDC_DECIMALS));
                setState((prev) => ({
                    ...prev,
                    status: 'success',
                    stepLabel: null,
                    txHashes: [...txHashes],
                    depositedUsd,
                }));
                toast.success(
                    `Deposited $${depositedUsd.toFixed(2)} from your wallet on HyperEVM.`,
                    { duration: TOAST_DURATION },
                );
                onSuccess?.();

                // HyperCore credits in a later Core block. The money has already left the
                // wallet and the panel refreshes on its own, so this only re-refreshes once the
                // credit is visible — it never gates or fails the deposit.
                void (async () => {
                    if (coreBefore === null) return;
                    const deadline = Date.now() + CORE_CREDIT_POLL_MS;
                    for (;;) {
                        await sleep(CORE_CREDIT_INTERVAL_MS);
                        try {
                            if ((await readCoreSpotUsdc8(adapter)) > coreBefore) {
                                onSuccess?.();
                                return;
                            }
                        } catch {
                            // transient info-API hiccup; the deadline still bounds this
                        }
                        if (Date.now() >= deadline) return;
                    }
                })();
            } catch (error) {
                const described = describeErrorForUser(error, {
                    operation: 'Deposit',
                });
                const message = userErrorText(described);
                console.error('[DirectDeposit] failed:', error);
                // the approval may already have gone through — refresh so the panel and the
                // allowance-aware step planning both reflect what really happened.
                onSuccess?.();
                setState((prev) => ({
                    ...prev,
                    status: 'failed',
                    error: message,
                    txHashes: [...txHashes],
                }));
                toast.error(described.title, {
                    description: message,
                    duration: TOAST_DURATION,
                });
            } finally {
                lockRef.current = false;
                // best-effort return to the anchor — everything else in the app
                // gates on it.
                switchChain(walletWagmiConfig, {
                    chainId: anchorChainId,
                }).catch(() => undefined);
            }
        },
        [address, anchorChainId, factionsApi, onSuccess],
    );

    return {
        state,
        run,
        reset,
        refresh,
        isRunning: state.status === 'preparing' || state.status === 'running',
        hypeGasOk,
        /** EOA USDC on HyperEVM (display units); null while loading. */
        balance,
        /** Same balance in base units — the MAX button and validation use this. */
        balance6,
        /** null while the route read is in flight. */
        route: (contextQuery.data?.route ?? null) as HyperDepositRoute | null,
        usdcAddress,
    };
}
